import * as React from "react";
import { Select, IOption } from "../../atoms/forms/Select";
import { ISelfManagedSelectControls } from "./ICommonInputsControls";
import { useTranslation } from 'react-i18next';

export type Gender = "Male" | "Female";

interface IGenderOption extends IOption {
    gender: Gender;
}

const genderOptions: IGenderOption[] = [
    { value: "Male", label: "Man", gender: "Male" },
    { value: "Female", label: "Vrouw", gender: "Female" }
];

// Props interface
interface IProps {
}

// Actual Component
export type AllProps = IProps & ISelfManagedSelectControls<Gender>;
export const GenderSelect = (props: AllProps) => {
    const { t } = useTranslation('client');
    
    // Internal state for selected gender
    const [selectedGender, setSelectedGender] = React.useState<IGenderOption | null>(() => {
        if (props.value) {
            return genderOptions.find(g => g.gender === props.value) || null;
        }
        return null;
    });

    // Update selected gender when value prop changes
    React.useEffect(() => {
        if (props.value) {
            const gender = genderOptions.find(g => g.gender === props.value);
            setSelectedGender(gender || null);
        } else {
            setSelectedGender(null);
        }
    }, [props.value]);

    // Translate labels
    const translatedOptions = React.useMemo(() => {
        return genderOptions.map(option => ({
            ...option,
            label: t(`gender.${option.gender}`, option.gender)
        }));
    }, [t]);

    // Update selected gender when translated options change
    React.useEffect(() => {
        if (selectedGender) {
            const translatedGender = translatedOptions.find(g => g.gender === selectedGender.gender);
            if (translatedGender && translatedGender.label !== selectedGender.label) {
                setSelectedGender(translatedGender);
            }
        }
    }, [translatedOptions]);

    // Handle change
    const handleChange = (newValue: IGenderOption | null, isValid: boolean) => {
        setSelectedGender(newValue);
        if (props.onChange) {
            props.onChange(newValue?.gender || null, isValid);
        }
    };

    // Wrap onValidate to work with IGenderOption
    const handleValidate = props.onValidate ? (option: IGenderOption | null): [boolean, string] => {
        return props.onValidate!(option?.gender || null);
    } : undefined;

    return (
        <Select
            label={props.label || t('gender', 'Geslacht')}
            value={selectedGender}
            options={genderOptions}
            loading={props.loading}
            disabled={props.disabled}
            required={props.required}
            error={props.error}
            placeholder={props.placeholder}
            prevalidate={props.prevalidate}
            icon={props.icon}
            onChange={handleChange}
            onValidate={handleValidate}
        />
    );
}

